import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Switch,
  FormControl,
  FormHelperText,
  FormLabel,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ConditionalTooltip from '../conditionalTooltip';
import Tooltip from '../tooltip';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import { isEmpty } from '../../../utility/validation';

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    flexShrink: 0,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 18,
    lineHeight: '20px',
    marginBottom: 0,
  },
  stackedLabel: {
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-word',
  },
  stackedLabelContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  description: {
    marginTop: spacing(1),
  },
  highlightedLabel: {
    fontFamily: 'ProximaNova-Bold',
  },
  tooltipIcon: {
    marginLeft: spacing(1),
    width: 14,
    height: 14,
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  contentStacked: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  contentWithDescription: {
    alignItems: 'end',
  },
  switchWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  switchIcon: {
    marginRight: spacing(1),
  },
  statusLabel: {
    marginLeft: spacing(2),
  },
}));

const CustomSwitch = ({
  className = undefined,
  labelClass = null,
  label = '',
  description = '',
  tooltipInfo = '',
  tooltipText = '',
  statusLabel='',
  disabledTooltipText = '',
  switchIcon: SwitchIcon = null,
  isHighlightedLabel = false,
  isStacked = false,
  isChecked,
  isDisabled = false,
  onChange,
}) => {
  const classes = useStyles();

  const handleChange = useCallback(
    event => {
      onChange(event.target.checked);
    },
    [onChange]
  );

  const handleClickPropagation = useCallback(event => {
    event.stopPropagation();
  }, []);

  const renderLabel = () => {
    if (!label) {
      return null;
    }

    if (isStacked) {
      return (
        <div className={classes.stackedLabelContainer}>
          <FormHelperText
            className={classNames(
              classes.stackedLabel,
              {
                [classes.highlightedLabel]: isHighlightedLabel,
              },
              labelClass
            )}
          >
            <span>{label}</span>
            {tooltipInfo && (
              <Tooltip
                customIconClass={classes.tooltipIcon}
                text={tooltipInfo}
              />
            )}
          </FormHelperText>
        </div>
      );
    }

    return (
      <FormLabel
        classes={{
          root: classNames(
            classes.label,
            {
              [classes.highlightedLabel]: isHighlightedLabel,
            },
            labelClass
          ),
        }}
      >
        <span>{label}</span>
        {tooltipInfo && (
          <Tooltip customIconClass={classes.tooltipIcon} text={tooltipInfo} />
        )}
      </FormLabel>
    );
  };

  return (
    <FormControl className={classNames(classNames.root, className)} fullWidth>
      <div
        className={classNames(classes.content, {
          [classes.contentWithDescription]: isEmpty(description) && !isStacked,
          [classes.contentStacked]: isStacked,
        })}
      >
        {description ? (
          <div>
            {renderLabel()}
            <TextBoxWithTooltip
              className={classes.description}
              variant="body2"
              text={description}
            />
          </div>
        ) : (
          renderLabel()
        )}
        <ConditionalTooltip
          message={
            isDisabled && disabledTooltipText
              ? disabledTooltipText
              : tooltipText
          }
          addTooltip={
            isDisabled && disabledTooltipText ? isDisabled : !!tooltipText
          }
        >
          <div className={classes.switchWrapper}>
            {SwitchIcon && <SwitchIcon className={classes.switchIcon} />}
            <Switch
              color="default"
              checked={isChecked}
              disabled={isDisabled}
              onChange={handleChange}
              onClick={handleClickPropagation}
            />
            {statusLabel ? (
              <Typography
                variant="body1"
                component="span"
                className={classes.statusLabel}
              >
                {statusLabel}
              </Typography>
            ) : null}
          </div>
        </ConditionalTooltip>
      </div>
    </FormControl>
  );
};

CustomSwitch.propTypes = {
  className: PropTypes.string,
  labelClass: PropTypes.string,
  description: PropTypes.string,
  label: PropTypes.string,
  statusLabel: PropTypes.string,
  tooltipText: PropTypes.string,
  disabledTooltipText: PropTypes.string,
  switchIcon: PropTypes.object,
  isChecked: PropTypes.bool.isRequired,
  isStacked: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isHighlightedLabel: PropTypes.bool,
  tooltipInfo: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default CustomSwitch;
